import { apiGet, apiPost, apiPut } from '~/utils/api'
import type { TradeInLocation, TradeInReturnMethod, TradeInShippingOption } from '~/utils/tradeInFulfillment'

export interface TradeInDeduction {
  id: string
  propertyValueId: string
  propertyName: string
  propertyNameTranslations?: Record<string, string> | null
  valueName: string
  valueNameTranslations?: Record<string, string> | null
  deductionAmount: number
}

export interface TradeInInspectionItem {
  propertyCode: string
  propertyName: string
  propertyNameTranslations: Record<string, string>
  valueCode: string
  valueName: string
  valueNameTranslations: Record<string, string>
  priceImpact: number | null
}

export interface TradeInInspectionEvidence {
  id: string
  url: string
  type: 'photo' | 'video' | 'screenshot' | string
  description?: string | null
  sortOrder?: number | null
}

export interface TradeInPublishedInspectionReport {
  reportNumber?: string | null
  publishedAt?: string | null
  completedAt?: string | null
  items: TradeInInspectionItem[]
  evidences: TradeInInspectionEvidence[]
}

export interface TradeInReturnPreference {
  method: TradeInReturnMethod | null
  status?: string | null
  address?: {
    id?: string | null
    fullName: string
    phone?: string | null
    addressLine1: string
    addressLine2?: string | null
    city: string
    state?: string | null
    postalCode: string
    countryCode: string
  } | null
  pickupLocation?: {
    id?: string | null
    name: string
    address: string
    city: string
    postalCode: string
    countryCode: string
  } | null
  preferenceConfirmedAt?: string | null
  shippingPayer?: string | null
  customerChargeStatus?: string | null
  version: number
}

export interface TradeInReturnShipment {
  attemptId: string
  attemptStatus: string
  trackingId?: string | null
  trackingNumber?: string | null
  carrierCode?: string | null
  trackingStatus?: string | null
}

export interface TradeInFulfillmentResponse {
  orderId: string
  orderStatus: string
  custodyStatus: string
  orderVersion: number
  returnMethod: TradeInReturnMethod | null
  returnStatus: string
  returnVersion: number
  allowedActions: string[]
  returnShipment?: TradeInReturnShipment | null
}

export interface TradeInOrderSummary {
  id: string
  orderNumber: string
  productName: string
  productImageUrl: string | null
  quotedPrice: number
  finalPrice: number | null
  currencyCode?: string | null
  status: string
  custodyStatus?: string | null
  trackingStatus?: string | null
  shippingOption: TradeInShippingOption
  needsCustomerAction?: boolean | null
  nextCustomerActionCode?: TradeInNextCustomerActionCode | null
  createdAt: string
  updatedAt: string
}

export interface TradeInOrderDetail extends TradeInOrderSummary {
  basePrice: number
  totalDeductions: number
  adjustmentReason: string | null
  trackingNumber: string | null
  carrierCode?: string | null
  paymentMethod: string | null
  paidAt: string | null
  shippedAt: string | null
  receivedAt: string | null
  inspectedAt: string | null
  quoteExpiresAt: string | null
  deductions: TradeInDeduction[]
  inspectionItems: TradeInInspectionItem[]
  inspectionReport?: TradeInPublishedInspectionReport | null
  receivingLocation?: TradeInLocation | null
  returnPreference?: TradeInReturnPreference | null
  allowedActions: string[]
  returnShipment?: TradeInReturnShipment | null
  version: number
}

export interface TradeInOrderPublicLookup {
  orderNumber: string
  productName: string
  status: string
  returnStatus?: string | null
  needsCustomerAction?: boolean | null
  createdAt: string
  updatedAt: string
}

/** @deprecated Use TradeInOrderSummary or TradeInOrderDetail explicitly. */
export type TradeInOrderListItem = TradeInOrderSummary

export interface CreateTradeInOrderRequest {
  productId: string
  selectedPropertyValueIds: string[]
  shippingOption: TradeInShippingOption
  shippingAddressId?: string
  dropOffLocationId?: string
  returnMethod?: TradeInReturnMethod
  returnAddressId?: string
  paymentMethod: string
  paymentAccount?: string
  paymentAccountHolderName?: string
}

export interface CancelTradeInOrderRequest {
  reason?: string
  expectedOrderVersion: number
  expectedReturnVersion: number
}

export interface AcceptTradeInAdjustmentRequest {
  expectedOrderVersion: number
  expectedReturnVersion: number
}

export interface RejectTradeInAdjustmentRequest {
  reason: string
  returnMethod?: TradeInReturnMethod
  returnAddressId?: string
  pickupLocationId?: string
  confirmReturnPreference: boolean
  expectedOrderVersion: number
  expectedReturnVersion: number
}

export type TradeInNextCustomerActionCode =
  | 'NONE'
  | 'MAIL_DEVICE'
  | 'DROP_OFF_DEVICE'
  | 'REVIEW_ADJUSTED_PRICE'
  | 'CONFIRM_RETURN_PREFERENCE'
  | 'PICK_UP_DEVICE'
  | 'CONTACT_SUPPORT_RETURN_EXCEPTION'

export interface UpdateTradeInReturnPreferenceRequest {
  returnMethod: TradeInReturnMethod
  returnAddressId?: string
  pickupLocationId?: string
  expectedOrderVersion: number
  expectedReturnVersion: number
}

export interface UpdateTradeInTrackingRequest {
  trackingNumber: string
  carrier?: string
  expectedOrderVersion: number
}

export function useTradeInOrderAPI() {
  async function findCreatedOrderByKey(key: string): Promise<TradeInOrderDetail | null> {
    return await apiGet<TradeInOrderDetail | null>('/api/app/v1/orders/trade-in/by-idempotency-key', {
      headers: { 'Idempotency-Key': key },
    })
  }
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function fetchOrders(): Promise<TradeInOrderSummary[]> {
    loading.value = true
    error.value = null
    try {
      return await apiGet<TradeInOrderSummary[]>('/api/app/v1/orders/trade-in')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function fetchOrder(id: string): Promise<TradeInOrderDetail> {
    loading.value = true
    error.value = null
    try {
      return await apiGet<TradeInOrderDetail>(`/api/app/v1/orders/trade-in/${id}`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function cancelOrder(id: string, request: CancelTradeInOrderRequest, idempotencyKey: string): Promise<TradeInFulfillmentResponse> {
    loading.value = true
    error.value = null
    try {
      return await apiPost<TradeInFulfillmentResponse>(`/api/app/v1/orders/trade-in/${id}/cancel`, request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function createOrder(request: CreateTradeInOrderRequest, idempotencyKey: string): Promise<TradeInOrderDetail> {
    loading.value = true
    error.value = null
    try {
      return await apiPost<TradeInOrderDetail>('/api/app/v1/orders/trade-in', request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function updateTrackingNumber(id: string, request: UpdateTradeInTrackingRequest, idempotencyKey: string): Promise<TradeInFulfillmentResponse> {
    loading.value = true
    error.value = null
    try {
      return await apiPut<TradeInFulfillmentResponse>(`/api/app/v1/orders/trade-in/${id}/ship`, request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function acceptAdjustment(id: string, request: AcceptTradeInAdjustmentRequest, idempotencyKey: string): Promise<TradeInFulfillmentResponse> {
    loading.value = true
    error.value = null
    try {
      return await apiPost<TradeInFulfillmentResponse>(`/api/app/v1/orders/trade-in/${id}/accept-adjustment`, request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function rejectAdjustment(id: string, request: RejectTradeInAdjustmentRequest, idempotencyKey: string): Promise<TradeInFulfillmentResponse> {
    loading.value = true
    error.value = null
    try {
      return await apiPost<TradeInFulfillmentResponse>(`/api/app/v1/orders/trade-in/${id}/reject-adjustment`, request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function updateReturnPreference(id: string, request: UpdateTradeInReturnPreferenceRequest, idempotencyKey: string): Promise<TradeInFulfillmentResponse> {
    loading.value = true
    error.value = null
    try {
      return await apiPut<TradeInFulfillmentResponse>(`/api/app/v1/orders/trade-in/${id}/return-preference`, request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function lookupOrder(orderNumber: string): Promise<TradeInOrderPublicLookup> {
    loading.value = true
    error.value = null
    try {
      return await apiGet<TradeInOrderPublicLookup>(
        `/api/app/v1/orders/trade-in/lookup?orderNumber=${encodeURIComponent(orderNumber)}`,
      )
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    findCreatedOrderByKey,
    loading,
    error,
    fetchOrders,
    fetchOrder,
    cancelOrder,
    createOrder,
    updateTrackingNumber,
    acceptAdjustment,
    rejectAdjustment,
    updateReturnPreference,
    lookupOrder,
  }
}
